import React from 'react';
import { Box, Card, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { t } from '../utils/theme';

const inrFormat = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 1234.5 -> "1,234.50"; null -> "—" */
export const num = (v: string | number | null | undefined) => (v == null || v === '' ? '—' : inrFormat.format(Number(v)));
/** Signed rupees: "+₹1,234.50" / "−₹200.00" */
export const inr = (v: string | number | null | undefined, signed = false) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  const sign = signed ? (n > 0 ? '+' : n < 0 ? '−' : '') : n < 0 ? '−' : '';
  return `${sign}₹${inrFormat.format(Math.abs(n))}`;
};

/** P&L value: colour + arrow + sign, so it never relies on colour alone. */
export const Pnl: React.FC<{ value: string | number | null | undefined; size?: 'sm' | 'md' | 'lg' }> = ({ value, size = 'sm' }) => {
  if (value == null || value === '') return <span style={{ color: t.text3 }}>—</span>;
  const n = Number(value);
  const color = n > 0 ? t.profit : n < 0 ? t.loss : t.text2;
  const fontSize = { sm: '0.875rem', md: { xs: '1.1rem', sm: '1.5rem' }, lg: { xs: '1.6rem', sm: '2rem' } }[size];
  const Arrow = n > 0 ? ArrowDropUp : n < 0 ? ArrowDropDown : null;
  return (
    <Box component="span" sx={{ color, fontWeight: 700, fontSize, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
      {Arrow && size !== 'sm' && <Arrow sx={{ fontSize: size === 'lg' ? 36 : 30, ml: -0.75, display: { xs: size === 'lg' ? 'inline-block' : 'none', sm: 'inline-block' } }} />}
      {inr(n, true)}
    </Box>
  );
};

export const Panel: React.FC<{
  title?: React.ReactNode; subtitle?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; pad?: boolean;
}> = ({ title, subtitle, action, children, pad = true }) => (
  <Card sx={{ height: '100%' }}>
    {(title || action) && (
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: 2.5, pt: 2, pb: pad ? 0 : 1.5 }}>
        <Box>
          {title && <Typography variant="h6">{title}</Typography>}
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {action}
      </Stack>
    )}
    <Box sx={pad ? { p: 2.5 } : undefined}>{children}</Box>
  </Card>
);

export const Kpi: React.FC<{
  label: string; value: React.ReactNode; sub?: React.ReactNode; icon?: React.ReactNode; children?: React.ReactNode;
}> = ({ label, value, sub, icon, children }) => (
  <Card sx={{ p: { xs: 1.75, sm: 2.25 }, height: '100%', minWidth: 0 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Typography variant="overline" sx={{ color: t.text3 }}>{label}</Typography>
      {icon && <Box sx={{ color: t.text3, display: 'flex' }}>{icon}</Box>}
    </Stack>
    <Box sx={{ fontSize: { xs: '1.15rem', sm: '1.5rem' }, fontWeight: 700, lineHeight: 1.3, mt: 0.5, overflowWrap: 'anywhere' }}>{value}</Box>
    {sub && <Typography variant="body2" sx={{ color: t.text2, mt: 0.5 }}>{sub}</Typography>}
    {children}
  </Card>
);

export const EmptyRow: React.FC<{ cols: number; text: string }> = ({ cols, text }) => (
  <TableRow>
    <TableCell colSpan={cols} sx={{ py: 4, textAlign: 'center', color: t.text3, border: 0 }}>{text}</TableCell>
  </TableRow>
);

/** Page heading row: title + subtitle left, actions right. */
export const PageHeader: React.FC<{ title: string; subtitle?: React.ReactNode; actions?: React.ReactNode }> = ({ title, subtitle, actions }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}
         spacing={2} sx={{ mb: 3 }}>
    <Box>
      <Typography variant="h5">{title}</Typography>
      {subtitle && <Typography variant="body2" sx={{ color: t.text2, mt: 0.5 }}>{subtitle}</Typography>}
    </Box>
    {actions && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{actions}</Stack>}
  </Stack>
);
